import { OnboardingHubEvent } from '../enum/enums';
import {
  CompletenessStatusItem,
  RawOnboardingHubPayload,
  RawReviewedEntity,
  AppStepReviewStatus,
  StepReviewState,
  StepReviewUpdate,
} from './onboarding-notification.interfaces';
import { resolveStepRoute } from './entity-step-mapping';

const REVIEW_EVENTS = new Set<string>([
  OnboardingHubEvent.EntityCompleted,
  OnboardingHubEvent.EntityIncomplete,
]);

export function isReviewNotification(raw: unknown): raw is RawOnboardingHubPayload {
  if (!raw || typeof raw !== 'object') return false;
  const obj = raw as Record<string, unknown>;
  return (
    typeof obj['notificationTypeName'] === 'string' &&
    REVIEW_EVENTS.has(obj['notificationTypeName'])
  );
}

function deriveState(notificationTypeName: string): StepReviewState {
  switch (notificationTypeName) {
    case OnboardingHubEvent.EntityCompleted:
      return 'approved';
    case OnboardingHubEvent.EntityIncomplete:
      return 'objected';
    default:
      return 'none';
  }
}

function extractEntities(raw: RawOnboardingHubPayload): RawReviewedEntity[] {
  const entities = raw.reviewDetail?.entities;
  return Array.isArray(entities) ? entities : [];
}

export function mapNotificationToStepUpdates(
  raw: unknown,
): StepReviewUpdate[] {
  if (!isReviewNotification(raw)) return [];

  const state = deriveState(raw.notificationTypeName);
  if (state === 'none') return [];

  const reviewedBy = raw.reviewDetail?.reviewedByRoleName ?? raw.performedByUserName ?? '';
  const performedAt = raw.performedAtUTC ?? '';

  const updates: StepReviewUpdate[] = [];

  for (const entity of extractEntities(raw)) {
    const stepRoute = resolveStepRoute(entity.entityName);
    if (!stepRoute) continue;

    updates.push({
      stepRoute,
      state,
      comment: entity.comment ?? null,
      entityName: entity.entityName,
      entityId: entity.entityId ?? 0,
      reviewedBy,
      performedAt,
    });
  }

  return updates;
}

export function applyStepUpdates(
  current: Readonly<Record<string, StepReviewState>>,
  updates: readonly StepReviewUpdate[],
): Record<string, StepReviewState> {
  const next = { ...current };
  const batchByStep: Record<string, StepReviewState> = {};

  for (const u of updates) {
    const existing = batchByStep[u.stepRoute];
    if (!existing || u.state === 'objected') {
      batchByStep[u.stepRoute] = u.state;
    }
  }

  for (const [stepRoute, state] of Object.entries(batchByStep)) {
    next[stepRoute] = state;
  }

  return next;
}

export function mapCompletenessToStepStatus(
  items: readonly CompletenessStatusItem[],
): AppStepReviewStatus {
  const next: Record<string, StepReviewState> = {};

  for (const item of items) {
    // const isInitialDraftIncomplete =
    //   item.reviewStatus == null &&
    //   item.reviewStatusName == null &&
    //   item.reviewedByRole == null &&
    //   item.reviewedByRoleName == null &&
    //   item.isComplete === false;

    for (const entity of item.entities ?? []) {
      const stepRoute = resolveStepRoute(entity.entityName);
      if (!stepRoute) continue;
      // Special case requested: when completeness returns an initial draft incomplete
      // entry for ApplicationDocument, show objected indicator on Documents step.
      const shouldMarkDraftDocumentsObjected = false;  
          // isInitialDraftIncomplete &&
          // stepRoute === 'documents' &&
          // entity.entityName === 'ApplicationDocument';

      const state: StepReviewState =
        item.reviewStatus === 1
          ? 'approved'
          : item.reviewStatus === 2
            ? 'objected'
            : item.reviewStatus == null
              ? shouldMarkDraftDocumentsObjected
                ? 'objected'
                : item.isComplete === true
                  ? 'approved'
                  : 'none'
              : 'none';

      const current = next[stepRoute];
      if (!current || state === 'objected') {
        next[stepRoute] = state;
      }
    }
  }

  return next;
}
